// src/utils/navigation/contentNavigation.ts 
// ==========================================================
//  TownLIT Content Navigation Engine
// ----------------------------------------------------------
//  Single source of truth for building /content URLs
// ==========================================================

import type {
  ContentMode,
  ContentShape,
  Aspect,
} from "../../types/content/viewer";

/* ----------------------------------------------------------
 * Navigation context (DSL)
 * ---------------------------------------------------------- */
export interface ContentNavContext {
  /** Content display mode */
  mode: ContentMode;

  /** Backend endpoint that owns this content */
  endpoint: string; // e.g. "/posts/moments", "/posts/testimonies"

  /** URL shape */
  scope?: ContentShape;

  /** Visitor profile username (required for profile scope) */
  username?: string;

  /** Optional UI hints */
  title?: string;
  aspect?: Aspect;
  focus?: string;
  lang?: string;

  /** Viewer shaping (profile vs flat rendering) */
  keyPath?: string;
  ownerPath?: string;

  /** Media hints */
  usePoster?: boolean;
}

/* ----------------------------------------------------------
 * Core URL builder
 * ---------------------------------------------------------- */
export function buildContentUrl(
  id: string,
  ctx: ContentNavContext
): string {
  const sp = new URLSearchParams();

  // Required
  sp.set("type", ctx.mode);
  sp.set("e", ctx.endpoint);

  // UI hints
  if (ctx.title) sp.set("t", ctx.title);
  if (ctx.aspect) sp.set("a", ctx.aspect);
  if (ctx.focus) sp.set("focus", ctx.focus);
  if (ctx.lang) sp.set("lang", ctx.lang);

  if (typeof ctx.usePoster === "boolean") {
    sp.set("p", ctx.usePoster ? "1" : "0");
  }

  // Shape hints
  if (ctx.scope === "profile") {
    sp.set("s", "profile");
    if (ctx.keyPath) sp.set("k", ctx.keyPath);
    if (ctx.ownerPath) sp.set("o", ctx.ownerPath);
  }

  const query = sp.toString();

  if (ctx.scope === "profile" && ctx.username) {
    return `/lit/${encodeURIComponent(ctx.username)}/content/${encodeURIComponent(
      id
    )}${query ? `?${query}` : ""}`;
  }

  return `/content/${encodeURIComponent(id)}${query ? `?${query}` : ""}`;
}

/* ----------------------------------------------------------
 * DX helpers
 * ---------------------------------------------------------- */

// ✅ Media (image | video)
export function makeMediaUrl(
  id: string,
  ctx: Omit<ContentNavContext, "mode">
): string {
  return buildContentUrl(id, { ...ctx, mode: "media" });
}

// 🔒 Legacy (keep for now – DO NOT USE IN NEW CODE)
export function makeVoiceUrl(
  id: string,
  ctx: Omit<ContentNavContext, "mode">
): string {
  return buildContentUrl(id, { ...ctx, mode: "voice" });
}

export function makeReadUrl(
  id: string,
  ctx: Omit<ContentNavContext, "mode">
): string {
  return buildContentUrl(id, { ...ctx, mode: "read" });
}
